import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { THEMES, setTheme, useTheme } from '../lib/theme';

/** Palette button that lets each player pick their own color theme (saved in their browser). */
export function ThemePicker({ className }: { className?: string }) {
  const current = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        onClick={() => setOpen((value) => !value)}
        title="Color theme"
        aria-expanded={open}
        className="glass grid size-10 place-items-center rounded-full text-paper transition hover:bg-ink-800"
      >
        <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3a9 9 0 1 0 0 18c1.1 0 1.7-.9 1.4-1.9-.3-1 .2-2.1 1.3-2.1H17a4 4 0 0 0 4-4c0-5.5-4-10-9-10Z" />
          <circle cx="7.5" cy="11" r="1.3" fill="currentColor" />
          <circle cx="10.5" cy="7" r="1.3" fill="currentColor" />
          <circle cx="15" cy="7.5" r="1.3" fill="currentColor" />
        </svg>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="glass-strong absolute top-12 right-0 z-50 w-[19rem] origin-top-right rounded-2xl p-1.5 shadow-2xl"
          >
            <p className="px-2.5 pt-1.5 pb-2 text-[10px] font-bold tracking-[0.2em] text-ink-400 uppercase">Color theme</p>
            <div className="grid grid-cols-2 gap-1">
              {THEMES.map((theme) => (
                <button
                  key={theme.id}
                  role="menuitemradio"
                  aria-checked={theme.id === current}
                  onClick={() => setTheme(theme.id)}
                  className={cn(
                    'flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm font-semibold ring-1 transition hover:bg-white/8',
                    theme.id === current ? 'bg-white/8 ring-primary-400/60' : 'ring-transparent',
                  )}
                >
                  <span className="flex shrink-0 -space-x-1.5">
                    {theme.swatch.map((color) => (
                      <span key={color} className="size-5 rounded-full ring-2 ring-ink-900" style={{ background: color }} />
                    ))}
                  </span>
                  <span className="truncate">{theme.name}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
